import { toSafeLocale } from './safeLocale';

/**
 * Get the user's browser locale with fallback to Argentina
 * Used for date/time formatting to respect user's locale preferences
 */
export const getUserLocale = (): string => {
  return toSafeLocale(typeof navigator !== 'undefined' ? navigator.language : undefined);
};

/**
 * Format a date string (YYYY-MM-DD) to user's locale date format
 * Uses the browser's locale to format dates correctly for each user
 *
 * Uses noon (12:00) local time to avoid timezone/day-boundary issues
 */
export const formatDateStringToLocaleDate = (dateString: string | null | undefined): string => {
  // Defensa: algunos registros (ej. recordatorios/eventos creados sin fecha) traen
  // date === null. Sin este guardia, dateString.split('-') revienta el render de toda
  // la lista. Devolvemos '' para no mostrar nada en lugar de crashear.
  if (!dateString) return '';
  // Normalizar por si llega un timestamp legacy (p.ej. "2026-10-15T15:00:00.000Z"
  // persistido con hora): sin esto, parseInt del día revienta con NaN → "Invalid Date".
  const normalized = normalizeFechaEvento(dateString);
  // Create Date at NOON local time to avoid UTC conversion issues
  // Using noon (12:00) instead of midnight prevents day shift problems in negative UTC offsets
  const parts = normalized.split('-');
  if (parts.length === 3) {
    const dateObject = new Date(
      parseInt(parts[0]),  // year
      parseInt(parts[1]) - 1,  // month (0-indexed)
      parseInt(parts[2]),  // day
      12,  // hour = noon (prevents day shift in timezones like UTC-3)
      0,  // minute
      0   // second
    );
    return dateObject.toLocaleDateString(getUserLocale(), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
  return dateString;
};

/**
 * Parsea un datetime string a Date, tratándolo como UTC cuando no tiene offset.
 *
 * Los datetimes guardados en Supabase/Postgres son UTC, pero pueden llegar sin
 * offset ("2026-10-15 14:30:00" con espacio, o ISO sin Z). `new Date()` parsea
 * esos formatos como hora LOCAL (spec/implementation-defined), con lo que la hora
 * UTC se muestra "como si fuera" local — el desfase reportado en la columna
 * "Actualización" (KAN-143). Con offset explícito (Z o ±HH:MM) se respeta tal cual.
 */
export const parseDateTimeAsUTC = (dateTimeString: string): Date => {
  const normalized = dateTimeString.trim().replace(' ', 'T');
  if (!/[Zz]|[+-]\d{2}:?\d{2}$/.test(normalized)) {
    return new Date(`${normalized}Z`);
  }
  return new Date(normalized);
};

/**
 * Format a datetime string (ISO format) to user's locale datetime format
 * Uses the browser's locale to format dates correctly for each user
 */
export const formatStringToLocaleDateTime = (dateTimeString: string): string => {
  if (!dateTimeString) return '';
  // Date-only: un día no tiene hora — mostrar como fecha, sin hora fantasma ni
  // corrimiento UTC (new Date('YYYY-MM-DD') cae en UTC-medianoche).
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateTimeString)) {
    return formatDateStringToLocaleDate(dateTimeString);
  }
  const dateObject = parseDateTimeAsUTC(dateTimeString);
  return dateObject.toLocaleString(getUserLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const createDate = (): string => {
    return getLocalISODate(new Date());
};

/**
 * Get local date in ISO format (YYYY-MM-DD) without timezone conversion.
 * Unlike toISOString(), this uses local date components, not UTC.
 *
 * Use this for date-only values that should remain in local timezone.
 * For timestamps/timestamps with time, use toISOString().
 *
 * @param date - Date object (defaults to current date)
 * @returns Date string in YYYY-MM-DD format using local timezone
 */
export const getLocalISODate = (date: Date = new Date()): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * Parsea una fecha de evento a Date en zona horaria LOCAL.
 *
 * `new Date('YYYY-MM-DD')` (sin hora) se interpreta como UTC: en Argentina
 * (UTC-3) cae a las 21:00 del día ANTERIOR y cualquier display o comparación
 * con horas locales corre un día (fechas "futuras" al cargar de noche,
 * recordatorios vencidos un día antes, labels un día menos). Este helper
 * fuerza el parseo local para date-only; los strings con hora van directo.
 */
export const getEventDate = (dateString: string): Date => {
    // Date-only (YYYY-MM-DD): append de T00:00:00 LOCAL (sin Z) para no caer en UTC.
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return new Date(dateString + "T00:00:00");
    }
    return new Date(dateString);
};

/**
 * Normalizes different date input formats to YYYY-MM-DD format (ISO date)
 * Supports: ISO (YYYY-MM-DD), DD-MM-YYYY, MM/DD/YYYY, and variations
 * Use this function to normalize user date inputs before storing them
 */
export const normalizeFechaEvento = (fechaStr: string): string => {
  if (!fechaStr) return '';

  // Handle common date formats and extract only YYYY-MM-DD
  // Examples: "2025-04-30T10:30:00", "30-04-2025", "04/30/2025", "2025-04-30 14:25:30"

  // First, try to parse as ISO date (YYYY-MM-DD or YYYY-MM-DDTHH:mm:ss)
  const isoDateMatch = fechaStr.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDateMatch) {
    return isoDateMatch[1];
  }

  // Try DD-MM-YYYY format
  const dmyMatch = fechaStr.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
  if (dmyMatch) {
    const [, day, month, year] = dmyMatch;
    const dayStr = day.padStart(2, '0');
    const monthStr = month.padStart(2, '0');
    return `${year}-${monthStr}-${dayStr}`;
  }

  // Try MM/DD/YYYY format
  const mdyMatch = fechaStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    const dayStr = day.padStart(2, '0');
    const monthStr = month.padStart(2, '0');
    return `${year}-${monthStr}-${dayStr}`;
  }

  // If we can't parse it, return as-is but warn
  console.warn(`Unable to parse fecha_de_evento: ${fechaStr}, using as-is`);
  return fechaStr;
};

/**
 * Determina si una fecha de evento es estrictamente posterior al día de hoy.
 * Comparación por DÍA (YYYY-MM-DD), no por timestamp, para evitar falsos
 * positivos por zona horaria. Acepta ISO con hora, ISO fecha y DD-MM-YYYY.
 *
 * @param fecha - Fecha a evaluar (vacío/null/undefined → false)
 * @param hoy - Fecha de referencia YYYY-MM-DD (inyectable para tests)
 */
export const isFechaEventoFutura = (
  fecha: string | null | undefined,
  hoy: string = getLocalISODate()
): boolean => {
  if (!fecha) return false;
  return normalizeFechaEvento(String(fecha)) > hoy;
};